import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { ArrowUpRight } from "lucide-react";

export function Services() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then(r => setProducts(r.data)); }, []);
  useSEO({
    title: "Services",
    description: "Three studios, one obsession — craft. Printing, graphic design, and website design.",
    breadcrumbs: [{ name: "Home", url: "/" }, { name: "Services", url: "/services" }],
  });
  const groups = [
    { key: "printing", label: "Printing", desc: "Business cards, flyers, posters, menus, invitations, postcards, t-shirts and more — produced on premium stock with meticulous color reproduction." },
    { key: "graphic_design", label: "Graphic Design", desc: "Logos, brand identity systems, business card design, flyer/poster design, menu design, and social media graphics." },
    { key: "website_design", label: "Website Design", desc: "Business websites, landing pages, portfolio websites, website redesign, and ongoing website maintenance." },
  ];
  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
      <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Services</p>
      <h1 className="font-serif text-5xl md:text-7xl mt-3">Everything we make.</h1>
      <p className="mt-8 text-[#555] max-w-2xl text-lg">From ink on paper to pixels on screen — our three studios collaborate on every project so your brand feels consistent everywhere it appears.</p>

      {groups.map((g, gi) => {
        const items = products.filter(p => p.parent_type === g.key);
        return (
          <div key={g.key} className="mt-24 border-t border-[#E5E5E5] pt-16">
            <div className="grid grid-cols-12 gap-6 mb-12">
              <p className="col-span-12 md:col-span-3 text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">0{gi+1}</p>
              <div className="col-span-12 md:col-span-9">
                <h2 className="font-serif text-4xl md:text-6xl">{g.label}</h2>
                <p className="mt-5 text-[#555] max-w-2xl">{g.desc}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {items.map(p => (
                <Link key={p.id} to={`/product/${p.slug}`} data-testid={`service-product-${p.slug}`} className="group block bg-white border border-[#E5E5E5] gold-border-hover">
                  <div className="aspect-square overflow-hidden bg-[#F5F5F5]">
                    <img src={p.image_url} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt={p.name} />
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif text-lg">{p.name}</h3>
                    <p className="font-mono text-xs mt-1 text-[#C5A059]">from ${p.base_price.toFixed(2)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        );
      })}

      <div className="mt-24 border border-[#E5E5E5] p-10 flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Something custom?</p>
          <h3 className="font-serif text-3xl mt-2">Request a quote in minutes.</h3>
        </div>
        <Link to="/quote" data-testid="services-quote-btn" className="inline-flex items-center gap-2 bg-[#0a0a0a] text-white hover:bg-[#C5A059] hover:text-[#0a0a0a] transition-colors px-8 py-4 text-xs tracking-[0.25em] uppercase font-bold">Request Quote <ArrowUpRight size={14} /></Link>
      </div>
    </section>
  );
}

export function Industries() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/industries").then(r => setItems(r.data)); }, []);
  useSEO({
    title: "Industries",
    description: "Print, design, and web solutions crafted for the industries we know best.",
    breadcrumbs: [{ name: "Home", url: "/" }, { name: "Industries", url: "/industries" }],
  });
  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
      <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Industries</p>
      <h1 className="font-serif text-5xl md:text-7xl mt-3">Who we serve.</h1>
      <p className="mt-8 text-[#555] max-w-2xl text-lg">Every industry has its own visual language. Ours is craft — carried through the details of the industries we know best.</p>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(ind => (
          <div key={ind.id} className="group border border-[#E5E5E5] gold-border-hover" data-testid={`industry-page-${ind.slug}`}>
            <div className="aspect-[16/10] overflow-hidden">
              <img src={ind.image_url} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt={ind.name} />
            </div>
            <div className="p-6">
              <h3 className="font-serif text-2xl">{ind.name}</h3>
              <p className="text-sm text-[#555] mt-2">{ind.description}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
